import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { TimeoutError, withTimeout } from "@/lib/withTimeout";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { 
  GraduationCap, 
  Users, 
  Wallet, 
  Share2,
  Link2,
  User as UserIcon,
  MessageCircle,
  ChevronDown,
  BarChart3,
  Layout,
  Video,
  Bot,
  Mail,
  Copy,
  Flame,
  CalendarDays,
  Send,
  ListTodo,
  FileText,
  Home,
  Bell,
  Settings,
  SlidersHorizontal,
  AlertTriangle,
  Globe,
  Briefcase,
  Megaphone,
  Link as LinkIcon,
  Wrench,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import type { User } from "@supabase/supabase-js";
import logoBlack from "@/assets/logo-black.png";
import { TelegramConnectModal } from "@/components/dashboard/TelegramConnectModal";
import { CreditsProvider } from "@/contexts/CreditsContext";
import { MobileNavigation } from "@/components/dashboard/MobileNavigation";
import { FEATURE_FLAGS } from "@/config/featureFlags";

const generateToken = (userId: string) => {
  const timestamp = Date.now();
  return btoa(`${userId}|${timestamp}`);
};

const QUERY_TIMEOUT_MS = 12_000;

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [backendUnavailable, setBackendUnavailable] = useState(false);
  const [backendMessage, setBackendMessage] = useState<string | null>(null);
  const [telegramConnected, setTelegramConnected] = useState(false);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [creatorsWithoutSocials, setCreatorsWithoutSocials] = useState<string[]>([]);
  const [cryptoWalletMissing, setCryptoWalletMissing] = useState(false);
  const [hasApplications, setHasApplications] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackTab, setFeedbackTab] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [tabVisibility, setTabVisibility] = useState<Record<string, boolean>>({ referrals: false });
  const [tabVisibilityLoaded, setTabVisibilityLoaded] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [hasAdminRole, setHasAdminRole] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("client-dashboard-sidebar-collapsed") === "true";
  });
  const [displayName, setDisplayName] = useState("");

  const handleBackendError = (err: unknown) => {
    const message = err instanceof Error ? err.message : String(err);
    const isConnectivityIssue =
      err instanceof TimeoutError ||
      (err instanceof TypeError && /failed to fetch/i.test(message)) ||
      /timeout/i.test(message);

    if (isConnectivityIssue) {
      setBackendUnavailable(true);
      setBackendMessage(
        "We cannot connect to the backend right now. Please try again."
      );
      toast({
        title: "Connection issue",
        description: "The backend is not responding or is temporarily offline. Please try again.",
        variant: "destructive",
      });
      return;
    }

    console.error("Dashboard backend error:", err);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        if (!session?.user) {
          navigate("/auth");
        }
      }
    );

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setUser(session?.user ?? null);
        if (!session?.user) {
          navigate("/auth");
        }
      })
      .catch((err) => {
        handleBackendError(err);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Fetch tab visibility settings
  useEffect(() => {
    const fetchTabSettings = async () => {
      try {
        const { data, error } = await withTimeout(
          supabase.from("dashboard_tab_settings").select("tab_id, is_visible"),
          QUERY_TIMEOUT_MS
        );

        if (error) throw error;
        if (data) {
          const visibility: Record<string, boolean> = {};
          data.forEach((item: any) => {
            visibility[item.tab_id] = item.is_visible;
          });
          setTabVisibility(visibility);
        }
      } catch (err) {
        handleBackendError(err);
      } finally {
        setTabVisibilityLoaded(true);
      }
    };
    fetchTabSettings();
  }, []);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;

      try {

        // Fetch telegram status and crypto wallet
        const { data: profile, error: profileError } = await withTimeout(
          supabase
            .from("profiles")
            .select("telegram_connected, crypto_wallet_address, first_name, last_name")
            .eq("user_id", user.id)
            .maybeSingle(),
          QUERY_TIMEOUT_MS
        );
        if (profileError) throw profileError;

        setTelegramConnected(profile?.telegram_connected ?? false);
        setCryptoWalletMissing(!profile?.crypto_wallet_address || profile.crypto_wallet_address.trim() === "");
        const nextDisplayName = [profile?.first_name, profile?.last_name]
          .filter(Boolean)
          .join(" ")
          .trim();
        setDisplayName(nextDisplayName || user.email?.split("@")[0] || "Client");

        // Check applications
        const { data: apps, error: appsError } = await withTimeout(
          supabase
            .from("applications")
            .select("id, status, creator:creators(id)")
            .eq("user_id", user.id),
          QUERY_TIMEOUT_MS
        );
        if (appsError) throw appsError;

        setHasApplications((apps?.length ?? 0) > 0);

        // Check socials
        const approvedCreatorIds = (apps || [])
          .filter((app: any) => app.status === "approved")
          .map((app: any) => app.creator?.id)
          .filter(Boolean);
        
        if (approvedCreatorIds.length > 0) {
          const { data: socialsData, error: socialsError } = await withTimeout(
            supabase
              .from("creator_socials")
              .select("creator_id, instagram, threads, twitter, dfans")
              .eq("user_id", user.id)
              .in("creator_id", approvedCreatorIds),
            QUERY_TIMEOUT_MS
          );
          if (socialsError) throw socialsError;

          const creatorsWithRequiredSocials = new Set(
            (socialsData || [])
              .filter(s => s.instagram && s.threads && s.twitter && s.dfans)
              .map(s => s.creator_id)
          );
          
          const missingCreators = approvedCreatorIds.filter(id => !creatorsWithRequiredSocials.has(id));
          setCreatorsWithoutSocials(missingCreators);
        }

        // Fetch unread notifications count
        const { count, error: notifError } = await withTimeout(
          supabase
            .from("notifications")
            .select("*", { count: "exact", head: true })
            .eq("user_id", user.id)
            .eq("is_read", false),
          QUERY_TIMEOUT_MS
        );
        if (notifError) throw notifError;

        setUnreadNotifications(count || 0);

        // Check if user has admin/editor/viewer role
        const { data: roleData, error: roleError } = await withTimeout(
          supabase.from("user_roles").select("role").eq("user_id", user.id),
          QUERY_TIMEOUT_MS
        );
        if (roleError) throw roleError;
        
        const hasRole = (roleData?.length ?? 0) > 0;
        setHasAdminRole(hasRole);
        
        // Store the actual role for display
        if (roleData && roleData.length > 0) {
          setUserRole(roleData[0].role);
        }
      } catch (err) {
        handleBackendError(err);
      }
    };

    fetchUserData();
  }, [user]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const displayInitials = (displayName || user?.email || "Client")
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "C";

  const sidebarRoleLabel =
    userRole === "admin" ? "Admin" : userRole === "editor" ? "Editor" : userRole === "viewer" ? "Viewer" : "Member";


  useEffect(() => {
    window.localStorage.setItem("client-dashboard-sidebar-collapsed", String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const sidebarSections = [
    {
      id: "home",
      label: "Home",
      icon: Home,
      items: [
        { id: "home", label: "Home", icon: Home, path: "/home" },
      ]
    },
    {
      id: "education",
      label: "Education",
      settingsKey: "education",
      items: [
        { id: "education", label: "Education", icon: GraduationCap, path: "/education", settingsKey: "education" },
        { id: "knowledge-bot", label: "Knowledge", icon: Bot, path: "/education/knowledge-bot", settingsKey: "knowledge-bot" },
      ]
    },
    {
      id: "creators",
      label: "Creators",
      items: [
        { id: "creators", label: "Your creators", icon: Users, path: "/creators" },
      ]
    },
    {
      id: "creating",
      label: "Creating",
      settingsKey: "creating",
      items: [
        { id: "video", label: "Video", icon: Video, path: "/creating/video", settingsKey: "video", badge: "Coming soon" },
        { id: "duplicating", label: "Duplicate", icon: Copy, path: "/duplicating", settingsKey: "duplicating", badge: "Coming soon" },
      ]
    },
    {
      id: "tools",
      label: "Tools",
      icon: Wrench,
      settingsKey: "social-media",
      items: [
        ...(FEATURE_FLAGS.dashboardCalendar
          ? [{ id: "calendar", label: "Calendar", icon: CalendarDays, path: "/calendar", settingsKey: "calendar" }]
          : []),
        ...(FEATURE_FLAGS.dashboardTracking
          ? [{ id: "tracking", label: "Tracking", icon: BarChart3, path: "/tracking", settingsKey: "tracking", badge: "Coming soon" }]
          : []),
        { id: "compass", label: "Compass", icon: Layout, path: "/compass", settingsKey: "compass", badge: FEATURE_FLAGS.dashboardCompass ? undefined : "Coming soon", disabled: !FEATURE_FLAGS.dashboardCompass },
        { id: "avatar", label: "Avatar", icon: UserIcon, path: "/avatar", badge: "Coming soon", disabled: true },
      ]
    },
  ];

  // Filter tabs to only show visible ones in feedback modal
  const allTabs = sidebarSections.flatMap(s => 
    s.items
      .filter((i: any) => {
        // If item has a settingsKey, check visibility
        if (i.settingsKey && tabVisibility[i.settingsKey] === false) {
          return false;
        }
        return true;
      })
      .map((i: any) => ({ label: i.label, id: i.id }))
  );

  const handleSendFeedback = async () => {
    if (!feedbackTab || !feedbackMessage.trim()) {
      toast({ title: "Please select a tab and enter your feedback", variant: "destructive" });
      return;
    }
    
    setSendingFeedback(true);
    
    try {
      const mailtoLink = `mailto:agency@modelaiagency.com?subject=Feedback: ${feedbackTab}&body=${encodeURIComponent(feedbackMessage)}`;
      window.open(mailtoLink, "_blank");
      
      toast({ title: "Email client opened" });
      setShowFeedbackModal(false);
      setFeedbackTab("");
      setFeedbackMessage("");
    } catch (error) {
      toast({ title: "Failed to open email", variant: "destructive" });
    } finally {
      setSendingFeedback(false);
    }
  };

  const getVisibleItems = (items: any[]) => {
    return items.filter(item => {
      if (!item.settingsKey) return true;
      return tabVisibility[item.settingsKey] !== false;
    });
  };

  const isSectionVisible = (section: any) => {
    // If section has a settingsKey, check visibility
    if (section.settingsKey && tabVisibility[section.settingsKey] === false) {
      return false;
    }
    return true;
  };

  const getVisibleSections = () => {
    return sidebarSections.filter(section => {
      // Check if section itself is hidden
      if (!isSectionVisible(section)) return false;
      // Check if section has any visible items
      const visibleItems = getVisibleItems(section.items);
      return visibleItems.length > 0;
    });
  };

  if (loading || !tabVisibilityLoaded) {
    return (
      <div className="min-h-screen min-h-svh h-screen h-svh bg-[#f5f5f7]" />
    );
  }

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (backendUnavailable) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="mx-auto w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-muted-foreground" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Tijdelijk verbindingsprobleem</h1>
          <p className="text-sm text-muted-foreground">
            {backendMessage ??
              "De app kan momenteel geen data ophalen. Dit is meestal tijdelijk."}
          </p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => window.location.reload()}>
              Opnieuw proberen
            </Button>
            <Button variant="outline" onClick={() => navigate("/auth")}>
              Naar login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Get current page title for mobile header
  const getPageTitle = () => {
    if (location.pathname === "/home" || location.pathname.startsWith("/home") || location.pathname.startsWith("/goals")) {
      return "Home";
    }
    if (location.pathname === "/notifications") {
      return "Notifications";
    }
    for (const section of sidebarSections) {
      for (const item of section.items) {
        if (isActiveRoute(item.path.split("?")[0])) {
          return item.label;
        }
      }
    }
    return "Dashboard";
  };

  return (
    <CreditsProvider userId={user?.id || ""}>
    {/*
      Use svh on mobile to avoid 100vh issues on Android Chrome/Samsung Internet
      (URL bar causing wrong height calculations and broken nested scroll areas).
    */}
    <div className="min-h-screen min-h-svh h-screen h-svh bg-[#f5f5f7] flex flex-col md:flex-row overflow-hidden">
      
      {/* Mobile Navigation */}
      <MobileNavigation
        user={user}
        pageTitle={getPageTitle()}
        hasAdminRole={hasAdminRole}
        userRole={userRole}
        unreadNotifications={unreadNotifications}
        tabVisibility={tabVisibility}
        telegramConnected={telegramConnected}
        hasApplications={hasApplications}
        creatorsWithoutSocials={creatorsWithoutSocials}
        onLogout={handleLogout}
        onOpenTelegramModal={() => setShowTelegramModal(true)}
        onOpenFeedbackModal={() => setShowFeedbackModal(true)}
      />
      
      <aside className={`relative hidden md:flex h-screen ${sidebarCollapsed ? "w-[76px]" : "w-[278px]"} bg-white border-r border-black/5 flex-col flex-shrink-0 transition-[width] duration-300 ease-out`}>
        <div className={`flex h-[78px] items-center border-b border-black/5 ${sidebarCollapsed ? "justify-center px-3" : "justify-start px-5"}`}>
          <a
            href="https://modelaiagency.com"
            target="_blank"
            rel="noreferrer"
            className={`group relative flex items-center rounded-2xl transition-all hover:opacity-90 ${sidebarCollapsed ? "justify-center" : "gap-3"}`}
          >
            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-[14px] bg-[#f5f5f7] ring-1 ring-black/5">
              <img src={logoBlack} alt="Model AI Agency" className="h-7 w-7 object-contain" />
            </div>
            {sidebarCollapsed && (
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setSidebarCollapsed(false);
                }}
                className="absolute inset-0 flex items-center justify-center rounded-[14px] bg-white/80 opacity-0 backdrop-blur-[1px] transition-opacity group-hover:opacity-100 focus:outline-none"
                aria-label="Expand sidebar"
              >
                <ChevronDown className="h-4 w-4 rotate-90 text-[#1d1d1f]" />
              </button>
            )}
          </a>
        </div>

        <button
          type="button"
          onClick={() => setSidebarCollapsed((current) => !current)}
          className={`absolute right-4 top-5 flex h-8 w-8 appearance-none items-center justify-center rounded-[10px] border border-black/15 bg-transparent text-[#6e6e73] shadow-none outline-none transition-all hover:bg-[#f7f7f8] hover:text-[#1d1d1f] focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 active:ring-0 active:outline-none ${
            sidebarCollapsed ? "pointer-events-none opacity-0" : ""
          }`}
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {sidebarCollapsed ? <PanelLeftOpen className="h-[15px] w-[15px] stroke-[1.8]" /> : <PanelLeftClose className="h-[15px] w-[15px] stroke-[1.8]" />}
        </button>

        <nav className={`flex-1 overflow-y-auto min-h-0 ${sidebarCollapsed ? "px-2.5 py-4" : "px-3 py-4"}`}>
          {getVisibleSections().map((section) => {
            const visibleItems = getVisibleItems(section.items);
            if (visibleItems.length === 0) return null;

            const sectionActive = visibleItems.some((item: any) => {
              const itemPath = item.path.split("?")[0];
              const itemQuery = item.path.includes("?") ? item.path.split("?")[1] : "";
              const currentQuery = location.search.replace("?", "");
              return (
                location.pathname === itemPath &&
                (itemQuery === "" ? currentQuery === "" || currentQuery === "filter=all" : currentQuery === itemQuery)
              ) || location.pathname.startsWith(itemPath + "/");
            });

            if (sidebarCollapsed) {
              const SectionIcon = section.icon || visibleItems[0].icon;

              if (section.id === "tools") {
                return (
                  <Tooltip key={section.id} delayDuration={100}>
                    <TooltipTrigger asChild>
                      <div
                        className={`mb-2 flex h-10 w-full items-center justify-center rounded-2xl ${
                          sectionActive ? "bg-primary/10 text-primary" : "text-[#8e8e93]"
                        }`}
                      >
                        <SectionIcon className="h-4 w-4" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right">Tools · Coming soon</TooltipContent>
                  </Tooltip>
                );
              }

              return visibleItems.map((item: any) => {
                const ItemIcon = item.icon;
                const itemPath = item.path.split("?")[0];
                const itemQuery = item.path.includes("?") ? item.path.split("?")[1] : "";
                const currentQuery = location.search.replace("?", "");
                const isActive =
                  location.pathname === itemPath &&
                  (itemQuery === "" ? currentQuery === "" || currentQuery === "filter=all" : currentQuery === itemQuery);

                return (
                  <Tooltip key={item.id} delayDuration={100}>
                    <TooltipTrigger asChild>
                      {item.disabled ? (
                        <div className="mb-2 flex h-10 w-full items-center justify-center rounded-2xl text-[#8e8e93]/50">
                          <ItemIcon className="h-4 w-4" />
                        </div>
                      ) : (
                        <Link
                          to={item.path}
                          className={`mb-2 flex h-10 w-full items-center justify-center rounded-2xl transition-all ${
                            isActive ? "bg-primary/10 text-primary" : "text-[#8e8e93] hover:bg-[#f5f5f7] hover:text-[#1d1d1f]"
                          }`}
                        >
                          <ItemIcon className="h-4 w-4" />
                        </Link>
                      )}
                    </TooltipTrigger>
                    <TooltipContent side="right">{item.label}</TooltipContent>
                  </Tooltip>
                );
              });
            }

            if (section.id === "tools") {
              return (
                <div key={section.id} className="mb-3">
                  <div className="flex w-full items-center justify-between rounded-2xl px-3 py-2 text-left">
                    <span className="flex items-center gap-2.5">
                      <Wrench className={`h-[16px] w-[16px] ${sectionActive ? "text-primary" : "text-[#1d1d1f]"}`} />
                      <span className="text-[14px] font-medium tracking-[-0.015em] text-[#1d1d1f]">{section.label}</span>
                    </span>
                    <span className="rounded-full bg-[#fff4e8] px-2.5 py-0.5 text-[10px] font-medium text-[#c47a2c] ring-1 ring-[#f1d3ad]">Coming soon</span>
                  </div>
                </div>
              );
            }

            return (
              <div key={section.id} className="mb-3">
                {visibleItems.map((item: any) => {
                  const itemPath = item.path.split("?")[0];
                  const itemQuery = item.path.includes("?") ? item.path.split("?")[1] : "";
                  const currentQuery = location.search.replace("?", "");
                  const isActive =
                    location.pathname === itemPath &&
                    (itemQuery === "" ? currentQuery === "" || currentQuery === "filter=all" : currentQuery === itemQuery);
                  const ItemIcon = item.icon;

                  const content = (
                    <>
                      {ItemIcon && <ItemIcon className={`h-[16px] w-[16px] ${isActive ? "text-primary" : "text-[#1d1d1f]"}`} />}
                      <span className="truncate">{item.label}</span>
                      {item.badge && <span className="ml-auto rounded-full bg-[#f5f5f7] px-2 py-0.5 text-[10px] font-medium text-[#8e8e93]">{item.badge}</span>}
                    </>
                  );

                  return item.disabled ? (
                    <div
                      key={item.id}
                      className="mb-0.5 flex w-full items-center gap-2.5 rounded-2xl px-3 py-2 text-[14px] font-medium tracking-[-0.015em] text-[#8e8e93]/70"
                      aria-disabled="true"
                    >
                      {content}
                    </div>
                  ) : (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={`mb-0.5 flex w-full items-center gap-2.5 rounded-2xl px-3 py-2 text-[14px] font-medium tracking-[-0.015em] transition-all ${
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "text-[#6e6e73] hover:bg-[#f5f5f7] hover:text-[#1d1d1f]"
                      }`}
                    >
                      {content}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className={`${sidebarCollapsed ? "px-2.5 py-3" : "px-3.5 py-3"} space-y-2`}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={sidebarCollapsed
                  ? "flex h-11 w-full items-center justify-center rounded-2xl border border-black/5 bg-white/90 text-[#1d1d1f] shadow-[0_8px_20px_rgba(0,0,0,0.05)] transition-all hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-black/10"
                  : "flex w-full items-center gap-3 rounded-[22px] border border-black/5 bg-white px-3 py-3 text-left shadow-[0_8px_20px_rgba(0,0,0,0.05)] transition-all hover:border-black/10 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-black/10"
                }
                aria-label="Open account menu"
              >
                <Avatar className="h-11 w-11 ring-1 ring-black/5">
                  <AvatarFallback className="bg-[#42d8cf] text-[14px] font-semibold text-white">
                    {displayInitials}
                  </AvatarFallback>
                </Avatar>
                {!sidebarCollapsed ? (
                  <>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">
                        {displayName || "Client"}
                      </p>
                      <p className="truncate text-[11px] text-[#8e8e93]">{sidebarRoleLabel}</p>
                    </div>
                    <ChevronDown className="h-4 w-4 flex-shrink-0 text-[#8e8e93]" />
                  </>
                ) : null}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="top"
              align={sidebarCollapsed ? "center" : "start"}
              sideOffset={10}
              className="w-[220px] rounded-2xl border border-black/5 bg-white p-2 shadow-[0_18px_50px_rgba(0,0,0,0.12)]"
            >
              <DropdownMenuLabel className="px-3 py-2 text-[11px] font-medium uppercase tracking-[0.12em] text-[#8e8e93]">
                Account
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => navigate("/dashboard/profile")}
                className="cursor-pointer rounded-xl px-3 py-2.5 text-[14px] font-medium text-[#1d1d1f] focus:bg-[#f5f5f7] focus:text-[#1d1d1f]"
              >
                <UserIcon className="mr-2.5 h-4 w-4 text-[#6e6e73]" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator className="mx-1 my-1 bg-black/6" />
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer rounded-xl px-3 py-2.5 text-[14px] font-medium text-[#d45454] focus:bg-[#fff1f1] focus:text-[#d45454]"
              >
                <LogOut className="mr-2.5 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {(hasApplications && !telegramConnected) || creatorsWithoutSocials.length > 0 ? (
            <>
              {hasApplications && !telegramConnected && (
                sidebarCollapsed ? (
                  <Tooltip delayDuration={100}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setShowTelegramModal(true)}
                        className="flex h-10 w-full items-center justify-center rounded-2xl bg-amber-500/10 text-amber-700 transition-colors hover:bg-amber-500/15"
                      >
                        <AlertTriangle className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Connect Telegram</TooltipContent>
                  </Tooltip>
                ) : (
                  <button
                    onClick={() => setShowTelegramModal(true)}
                    className="w-full flex items-center gap-2 rounded-2xl bg-amber-500/10 px-3 py-2.5 text-left text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-500/15"
                  >
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    Connect Telegram
                  </button>
                )
              )}

              {creatorsWithoutSocials.length > 0 && (
                sidebarCollapsed ? (
                  <Tooltip delayDuration={100}>
                    <TooltipTrigger asChild>
                      <Link
                        to="/creators/approved"
                        className="flex h-10 w-full items-center justify-center rounded-2xl bg-red-500/10 text-red-700 transition-colors hover:bg-red-500/15"
                      >
                        <AlertTriangle className="h-4 w-4" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right">{creatorsWithoutSocials.length} missing socials</TooltipContent>
                  </Tooltip>
                ) : (
                  <Link
                    to="/creators/approved"
                    className="w-full flex items-center gap-2 rounded-2xl bg-red-500/10 px-3 py-2.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-500/15"
                  >
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    {creatorsWithoutSocials.length} missing socials
                  </Link>
                )
              )}
            </>
          ) : null}

        </div>
      </aside>

      {/* Main Content */}
      <main className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-[#f5f5f7]">
        <div className="flex min-h-full flex-1 flex-col px-4 py-4 md:px-7 md:py-7">
          <Outlet
            context={{
              user,
              hasAdminRole,
              userRole,
              openFeedback: () => setShowFeedbackModal(true),
              logout: handleLogout,
            }}
          />
        </div>
      </main>

      {/* Telegram Connect Modal */}
      {user && (
        <TelegramConnectModal
          open={showTelegramModal}
          onOpenChange={setShowTelegramModal}
          telegramConnectUrl={`https://t.me/modelaiagency_agent_bot?start=${generateToken(user.id)}`}
          userId={user.id}
        />
      )}

      {/* Feedback Modal */}
      <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Feedback</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Select tab</Label>
              <Select value={feedbackTab} onValueChange={setFeedbackTab}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a tab..." />
                </SelectTrigger>
                <SelectContent>
                  {allTabs.map((tab) => (
                    <SelectItem key={tab.id} value={tab.label}>
                      {tab.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Your feedback</Label>
              <Textarea
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                placeholder="Write your feedback here..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedbackModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendFeedback} disabled={sendingFeedback}>
              <Send className="w-4 h-4 mr-2" />
              Send Feedback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </CreditsProvider>
  );
};

export default DashboardLayout;
